import { FieldPolicy, FieldReadFunction, TypePolicies } from '@apollo/client';
import { isPlainObject } from 'lodash';

/**
 * Custom merge strategy for TypePolicies:
 * - Replace properties in generated policies with handwritten ones
 * - Properly merge nested field configurations
 *
 * @param generated The generated type policies
 * @param handwritten The handwritten type policies to prioritize
 * @returns The merged type policies
 */
export function mergeTypePolicies(
  generated: TypePolicies,
  handwritten: TypePolicies
): TypePolicies {
  const result: TypePolicies = { ...generated };

  // Process each type in the handwritten policies
  Object.entries(handwritten).forEach(([typeName, handwrittenTypePolicy]) => {
    if (!result[typeName]) {
      // If type doesn't exist in generated, add it directly
      result[typeName] = handwrittenTypePolicy;
      return;
    }

    // Type exists in both, merge them with handwritten taking precedence
    result[typeName] = {
      ...result[typeName],
      ...handwrittenTypePolicy,
      // Handle fields separately to ensure proper merging
      fields: mergeFields(
        result[typeName]?.fields || {},
        handwrittenTypePolicy?.fields || {}
      ),
    };
  });

  return result;
}

/**
 * Merges field configurations, giving precedence to handwritten fields
 *
 * @param generatedFields Field configurations from generated type policies
 * @param handwrittenFields Field configurations from handwritten type policies
 * @returns Merged field configurations
 */
function mergeFields(
  generatedFields: Record<
    string,
    FieldPolicy<unknown, unknown, unknown> | FieldReadFunction<unknown, unknown>
  >,
  handwrittenFields: Record<
    string,
    FieldPolicy<unknown, unknown, unknown> | FieldReadFunction<unknown, unknown>
  >
): Record<
  string,
  FieldPolicy<unknown, unknown, unknown> | FieldReadFunction<unknown, unknown>
> {
  const result = { ...generatedFields };

  // Process each field in handwritten fields
  Object.entries(handwrittenFields).forEach(([fieldName, handwrittenField]) => {
    if (!result[fieldName]) {
      // If field doesn't exist in generated, add it directly
      result[fieldName] = handwrittenField;
      return;
    }

    if (isPlainObject(result[fieldName]) && isPlainObject(handwrittenField)) {
      // For objects like FieldPolicy, merge them with handwritten taking precedence
      result[fieldName] = mergeDeep(
        result[fieldName] as Record<string, unknown>,
        handwrittenField as Record<string, unknown>
      );
    } else {
      // For non-objects or incompatible types (like pure functions), use handwritten value
      result[fieldName] = handwrittenField;
    }
  });

  return result;
}

/**
 * Deep merges objects, giving priority to values from the second object
 *
 * @param target The target object to merge into
 * @param source The source object to merge from, taking precedence
 * @returns The merged object
 */
function mergeDeep(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result = { ...target };

  // Process each property in the source
  Object.entries(source).forEach(([key, sourceValue]) => {
    // If property doesn't exist in target, add it directly
    if (!(key in result)) {
      result[key] = sourceValue;
      return;
    }

    const targetValue = result[key];

    // If both values are plain objects, recursively merge them
    if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
      result[key] = mergeDeep(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>
      );
    } else {
      // Otherwise, source value takes precedence
      result[key] = sourceValue;
    }
  });

  return result;
}
